import { bugsnagNotify } from './bugsnag';

interface Choice {
  id: number;
  text: string;
}

interface Options {
  bank: HTMLElement;
  targets: HTMLElement[];
  choices: Choice[];
  promptId: number;
  answerId: number;
}

// A page-wide ceiling also bounds reports when practice navigation mounts new questions.
let reportsSent = 0;

export function observeInlineDrag(options: Options): () => void {
  const { bank, targets, choices, promptId, answerId } = options;
  const reported = new Set<string>();
  const timers = new Set<number>();
  let missedStarts = 0;
  let missedDrops = 0;
  let press: { x: number; y: number; started: boolean } = null;
  let drag: { startedAt: number; enteredTarget: boolean } = null;

  const targetAt = (node: EventTarget): number => node instanceof Node ?
    targets.findIndex((target) => target.contains(node)) : -1;
  const isOurs = (node: EventTarget): boolean => node instanceof Node &&
    (bank.contains(node) || targetAt(node) !== -1);

  const later = (callback: () => void) => {
    const timer = window.setTimeout(() => {
      timers.delete(timer);
      safely(callback);
    }, 0);
    timers.add(timer);
  };

  // Observers must never affect the interaction, including when the reporter is blocked.
  const safely = (callback: () => void) => {
    try { callback(); } catch (_) { /* Diagnostics are best effort. */ }
  };

  const report = (reason: string, details: Record<string, unknown>) => {
    if (reported.has(reason) || reportsSent >= 10) return;
    const error = new Error(`Inline drag diagnostic: ${reason}`);
    error.name = 'InlineDragDiagnostic';
    if (bugsnagNotify(error, (event) => {
      event.severity = 'warning';
      event.groupingHash = `inline-drag-v1:${reason}`;
      event.addMetadata('inlineDrag', {
        version: 1, reason, promptId, answerId, interactionMode: 'desktop-drag',
        targetCount: targets.length, choiceCount: choices.length, ...details
      });
    })) {
      reported.add(reason);
      reportsSent += 1;
    }
  };

  const mouseDown = (event: MouseEvent) => {
    press = null;
    drag = null;
    if (event.button !== 0 || !isOurs(event.target) || !(event.target instanceof Element)) return;
    if (!event.target.closest('.draggable-text[draggable="true"]')) return;
    press = { x: event.clientX, y: event.clientY, started: false };
  };

  const mouseUp = (event: MouseEvent) => {
    const attempt = press;
    press = null;
    if (!attempt || attempt.started || targetAt(event.target) === -1) return;
    if (Math.hypot(event.clientX - attempt.x, event.clientY - attempt.y) < 10) return;
    missedStarts += 1;
    if (missedStarts >= 3) report('repeated_missing_dragstart', {
      attempts: missedStarts, targetIndex: targetAt(event.target)
    });
  };

  const dragStart = (event: DragEvent) => {
    if (!isOurs(event.target)) return;
    if (press) press.started = true;
    drag = { startedAt: Date.now(), enteredTarget: false };
    // Read after the component and any extension handlers have run.
    later(() => {
      if (event.defaultPrevented) report('dragstart_prevented', { defaultPrevented: true });
    });
  };

  const dragOver = (event: DragEvent) => {
    if (drag && targetAt(event.target) !== -1) drag.enteredTarget = true;
  };

  const drop = (event: DragEvent) => {
    const targetIndex = targetAt(event.target);
    if (targetIndex === -1) return;
    const attempt = drag;
    drag = null;
    press = null;
    const transfer = event.dataTransfer;
    let raw = '';
    try {
      raw = transfer ? transfer.getData('text/plain') : '';
    } catch (_) {
      report('drop_data_unreadable', { targetIndex, localDragObserved: !!attempt });
      return;
    }
    let payload: Choice;
    let payloadStatus = raw ? 'invalid_json' : 'empty';
    try {
      payload = JSON.parse(raw);
      payloadStatus = payload && typeof payload.id === 'number' && typeof payload.text === 'string' ?
        'unknown_choice' : 'invalid_shape';
    } catch (_) { /* Do not report parser messages, which can contain dragged text. */ }
    const choice = payloadStatus === 'unknown_choice' && choices.find((candidate) => (
      candidate.id === payload.id && candidate.text === payload.text
    ));
    if (choice) payloadStatus = 'known_choice';
    const details = {
      targetIndex, payloadStatus, payloadLength: raw.length,
      // Avoid recording arbitrary MIME strings from external drag sources.
      hasPlainText: !!transfer && Array.from(transfer.types).includes('text/plain'),
      typeCount: transfer ? transfer.types.length : 0,
      effectAllowed: transfer?.effectAllowed, dropEffect: transfer?.dropEffect,
      localDragObserved: !!attempt, enteredTarget: !!attempt?.enteredTarget,
      durationMs: attempt ? Date.now() - attempt.startedAt : null
    };
    if (!choice) {
      report('unrecognized_drop_payload', details);
      return;
    }
    later(() => {
      if (!targets[targetIndex].isConnected) return;
      const input = targets[targetIndex].querySelector('input[type="hidden"]') as HTMLInputElement;
      if (!input || input.value !== choice.text) {
        report('placement_not_observed', {
          ...details, choiceId: choice.id, dropDefaultPrevented: event.defaultPrevented
        });
      } else {
        missedStarts = 0;
        missedDrops = 0;
      }
    });
  };

  const dragEnd = (event: DragEvent) => {
    if (!isOurs(event.target) || !drag) return;
    const attempt = drag;
    drag = null;
    press = null;
    const targetIndex = targetAt(document.elementFromPoint(event.clientX, event.clientY));
    // A cancelled drag away from a blank is normal. Repeated releases over a blank
    // without a drop are a diagnostic signal, not proof of an application bug.
    if (targetIndex === -1) return;
    missedDrops += 1;
    if (missedDrops >= 3) report('repeated_missing_drop', {
      attempts: missedDrops, targetIndex, enteredTarget: attempt.enteredTarget,
      dropEffect: event.dataTransfer?.dropEffect
    });
  };

  const listeners: { name: string; listener: (event: Event) => void }[] = [
    { name: 'mousedown', listener: mouseDown }, { name: 'mouseup', listener: mouseUp },
    { name: 'dragstart', listener: dragStart }, { name: 'dragover', listener: dragOver },
    { name: 'drop', listener: drop }, { name: 'dragend', listener: dragEnd }
  ];
  const observers = listeners.map(({ name, listener }) => {
    const observer = (event: Event) => safely(() => listener(event));
    document.addEventListener(name, observer, true);
    return { name, observer };
  });
  return () => {
    observers.forEach(({ name, observer }) => document.removeEventListener(name, observer, true));
    timers.forEach((timer) => window.clearTimeout(timer));
  };
}
